import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";

import type { AdminOrganization } from "@/api/client";
import { useOrganizations } from "@/api/hooks";
import { AppShell } from "@/components/app-shell";

/**
 * THE CUSTOMER LIST — every tenant on the platform.
 *
 * This screen is the reason the whole surface is audited. Loading it writes a row to the
 * append-only audit log (`org.listed`), because §5 says every USE of the cross-tenant role
 * is recorded, not merely every write. Pulling up the customer list is a use.
 */
export function OrganizationsPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const { data, isPending, error } = useOrganizations(search, status);

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Organizations</h1>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Every tenant on the platform. This view is audited.
          </p>
        </div>

        <div className="flex gap-2">
          <label className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-[var(--color-faint)]"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or slug"
              aria-label="Search organizations"
              className="w-56 rounded border border-[var(--color-rule-strong)] bg-[var(--color-panel)] py-2 pl-8 pr-3 text-sm text-[var(--color-text)]"
            />
          </label>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by status"
            className="rounded border border-[var(--color-rule-strong)] bg-[var(--color-panel)] px-3 py-2 text-sm text-[var(--color-text)]"
          >
            <option value="">Any status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {error && (
        <p role="alert" className="panel mt-6 p-4 text-sm text-[var(--color-dry)]">
          {error.message}
        </p>
      )}

      <div className="panel mt-6 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-[var(--color-rule)] text-xs uppercase tracking-wide text-[var(--color-faint)]">
            <tr>
              <th className="px-4 py-3 font-medium">Organization</th>
              <th className="px-4 py-3 font-medium">Plan</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Health</th>
              <th className="px-4 py-3 text-right font-medium">Sites</th>
              <th className="px-4 py-3 text-right font-medium">Refills 30d</th>
              <th className="px-4 py-3 font-medium">Owner</th>
            </tr>
          </thead>
          <tbody>
            {isPending && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-[var(--color-muted)]">
                  Loading…
                </td>
              </tr>
            )}

            {data?.data.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-[var(--color-muted)]">
                  No organizations match.
                </td>
              </tr>
            )}

            {data?.data.map((org) => (
              <tr key={org.id} className="border-b border-[var(--color-rule)] last:border-0">
                <td className="px-4 py-3">
                  <Link
                    to="/organizations/$orgId"
                    params={{ orgId: org.id }}
                    className="font-medium text-[var(--color-text)] underline-offset-2 hover:underline"
                  >
                    {org.name}
                  </Link>
                  <div className="tabular text-xs text-[var(--color-faint)]">{org.slug}</div>
                </td>
                <td className="px-4 py-3 capitalize text-[var(--color-muted)]">{org.plan}</td>
                <td className="px-4 py-3">
                  <StatusPill status={org.status} />
                </td>
                <td className="px-4 py-3">
                  <HealthPill health={org.health} />
                </td>
                <td className="tabular px-4 py-3 text-right">{org.usage.sites}</td>
                <td className="tabular px-4 py-3 text-right">{org.usage.refills_last_30d}</td>
                <td className="px-4 py-3 text-[var(--color-muted)]">{org.owner?.email ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data?.hasMore && (
        <p className="mt-3 text-xs text-[var(--color-faint)]">
          More organizations exist beyond this page. Narrow the search to find a specific tenant —
          this list is deliberately not "load everything".
        </p>
      )}
    </AppShell>
  );
}

export function StatusPill({ status }: { status: AdminOrganization["status"] }) {
  const styles: Record<string, string> = {
    active: "bg-[var(--color-wet-wash)] text-[var(--color-wet)]",
    suspended: "bg-[var(--color-warn-wash)] text-[var(--color-warn)]",
  };
  return (
    <span
      className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${styles[status] ?? "bg-[var(--color-panel-high)] text-[var(--color-muted)]"}`}
    >
      {status}
    </span>
  );
}

export function HealthPill({ health }: { health: AdminOrganization["health"] }) {
  if (!health) return <span className="text-[var(--color-faint)]">—</span>;

  const label: Record<string, string> = {
    healthy: "Healthy",
    idle: "Idle",
    at_risk: "At risk",
  };
  const styles: Record<string, string> = {
    healthy: "text-[var(--color-wet)]",
    idle: "text-[var(--color-faint)]",
    at_risk: "text-[var(--color-warn)]",
  };
  return <span className={`text-xs font-medium ${styles[health]}`}>{label[health]}</span>;
}
